/* global BigInt */

import { FEE_HOOK_ADDRESSES } from './feeHook';
import { getFormattedFinalityTime } from './finality';

// CCTP Fee Configuration based on Circle's official fee structure
// All fees are in basis points (bps) - 100 bps = 1%

export const CCTPFees = {
  // Standard transfer fees (all chains have 0 fee for standard)
  standard: {
    1: 0,     // Ethereum Mainnet
    10: 0,    // Optimism
    137: 0,   // Polygon
    8453: 0,  // Base
    42161: 0, // Arbitrum One
    43114: 0, // Avalanche C-Chain
    130: 0,   // Unichain
    59144: 0, // Linea
    146: 0,   // Sonic
  },
  
  // Fast transfer fees (only for supported chains)
  fast: {
    1: 1,        // Ethereum: 1 bps (0.01%)
    10: 1,       // OP Mainnet: 1 bps (0.01%)
    8453: 1,     // Base: 1 bps (0.01%)
    42161: 1,    // Arbitrum: 1 bps (0.01%)
    130: 1,      // Unichain: 1 bps (0.01%)
    59144: 14,   // Linea: 14 bps (0.14%)
    // Note: Codex would be 1 bps but chain ID needed
  },
  
  // Chains that support fast transfers
  fastSupported: {
    1: true,      // Ethereum
    10: true,     // OP Mainnet (Optimism)
    137: false,   // Polygon (already fast)
    8453: true,   // Base
    42161: true,  // Arbitrum
    43114: false, // Avalanche (already fast)
    130: true,    // Unichain
    59144: true,  // Linea
    146: false,   // Sonic (already fast)
    // Note: Codex supports fast transfers but chain ID needed
  }
};

// Helper function to calculate fees including both Circle and platform fees
export const calculateCCTPFees = (amount, sourceChainId, isFastTransfer = false, destinationChainId = null) => {
  const amountBN = BigInt(amount);
  
  // Check if fast transfer is requested and supported
  const actuallyFast = isFastTransfer && CCTPFees.fastSupported[sourceChainId];
  
  // Get Circle's fee rate in bps
  const circleFeeRate = actuallyFast 
    ? CCTPFees.fast[sourceChainId] || 0
    : CCTPFees.standard[sourceChainId] || 0;
  
  // Calculate Circle's fee: amount * feeRate / 10000
  // Always round UP to avoid insufficient_fee errors
  const circleFeeExact = (amountBN * BigInt(circleFeeRate));
  const circleFee = (circleFeeExact / BigInt(10000)) + (circleFeeExact % BigInt(10000) > 0n ? 1n : 0n);
  
  // Amount after Circle's fee
  const amountAfterCircleFee = amountBN - circleFee;
  
  // Get platform fee if destination uses fee hook
  let platformFeeRate = 0;
  let platformFee = BigInt(0);
  
  if (destinationChainId && FEE_HOOK_ADDRESSES[destinationChainId]) {
    const feeHookConfig = FEE_HOOK_ADDRESSES[destinationChainId];
    if (feeHookConfig.address) {
      // Platform fee is applied on the amount after Circle's fee
      // Also round UP for consistency
      platformFeeRate = feeHookConfig.feePercentage || 0;
      const platformFeeExact = (amountAfterCircleFee * BigInt(platformFeeRate));
      platformFee = (platformFeeExact / BigInt(10000)) + (platformFeeExact % BigInt(10000) > 0n ? 1n : 0n);
    }
  }
  
  // Total fees and final amount
  const totalFee = circleFee + platformFee;
  const totalFeeRate = circleFeeRate + platformFeeRate;
  const amountAfterAllFees = amountAfterCircleFee - platformFee;
  
  return {
    // Legacy fields for backwards compatibility
    fee: totalFee.toString(),
    feeRate: totalFeeRate,
    amountAfterFee: amountAfterAllFees.toString(),
    isFastTransfer: actuallyFast,
    
    // Detailed fee breakdown
    circleFee: circleFee.toString(),
    circleFeeRate,
    platformFee: platformFee.toString(),
    platformFeeRate,
    totalFee: totalFee.toString(),
    totalFeeRate,
    amountAfterCircleFee: amountAfterCircleFee.toString(),
    amountAfterAllFees: amountAfterAllFees.toString()
  };
};

// Get finality threshold based on transfer type
export const getFinalityThreshold = (isFastTransfer) => {
  return isFastTransfer ? 1000 : 2000; // Fast = 1000, Standard = 2000
};

// Get estimated transfer time
export const getEstimatedTime = (chainId, isFastTransfer) => {
  // Use the finality module for accurate time estimates
  return getFormattedFinalityTime(chainId, isFastTransfer ? 'fast' : 'typical');
};